// @ts-strict-ignore
import {diffFromNow, getNow, PERFORMANCE_EVENT_PREFIX} from 'common/util/timing';
import {getLocation} from 'common/util/url';

import {reporter} from './error-reporting';
import {getProjectId, getReportName, getWorkspaceId} from './util';

const START = `start`;
const END = `end`;

const perfMeasureMetadata = new Map<string, any>();

export class PerfMeasure {
  declare public measureName: string;
  declare public started: boolean;
  declare public ended: boolean;
  declare public duration: null | number;

  constructor(measureName: string) {
    this.measureName = measureName;
    this.started = false;
    this.ended = false;
    this.duration = null;
  }

  start() {
    if (!this.started) {
      this._perfMark(START);
      this.started = true;
    }
    return this;
  }

  end(metadata?: any) {
    if (this.started && !this.ended) {
      this._perfMark(END);
      this._perfMeasure();
      this.ended = true;
      if (metadata) {
        perfMeasureMetadata.set(this.measureName, metadata);
      }
    }
    return this;
  }

  _perfMark(markType) {
    if (typeof performance !== `undefined` && performance.mark) {
      performance.mark(`${this.measureName}:${markType}`);
    }
  }

  _perfMeasure() {
    if (typeof performance !== `undefined` && performance.measure) {
      const measurement = performance.measure(
        this.measureName,
        `${this.measureName}:${START}`,
        `${this.measureName}:${END}`,
      );
      // firefox will return undefined even though the measure function exists
      this.duration = measurement ? measurement.duration : null;
    }
  }

  static getMetadataFor(measureName: string) {
    return perfMeasureMetadata.get(measureName);
  }
}

/**
 * wait for the page content to render i.e user has something useful to look at
 * this correlates very well with perceived loading time
 * see: https://calendar.perfplanet.com/2012/moving-beyond-window-onload/
 * @param maxWaitMs - time limit for exit since browsers sometimes get stuck
 */
export function waitUntilUiReady(maxWaitMs = 20 * 1000) {
  return new Promise((resolve) => {
    const startTime = getNow();
    const testUIReady = () => {
      const paddingWrapper = document.querySelector(`[elref="pageContentContainer"]`);
      const exceededWaitLimit = diffFromNow(startTime) > maxWaitMs;
      if ((paddingWrapper && paddingWrapper.clientHeight > 200) || exceededWaitLimit) {
        if (!exceededWaitLimit) {
          // wait one more frame for browser to render ui
          requestAnimationFrame(resolve);
        }
      } else {
        requestAnimationFrame(testUIReady);
      }
    };

    testUIReady();
  });
}

// TODO opslead
function getPageLoadPerfProps() {
  const performance = window.performance;
  const location = getLocation();
  const trackProps = {};
  if (performance && performance.timing && performance.getEntriesByType && performance.timing.loadEventStart) {
    // track page url location parts
    trackProps[`location_href`] = location.href;
    trackProps[`location_host`] = location.host;
    trackProps[`location_hash`] = location.hash;
    trackProps[`location_path`] = location.pathname;
    trackProps[`location_path_pattern`] = location.pathname.replace(/\d+/g, `<id>`).replace(/\/$/, ``);

    const timing = performance.timing.toJSON();
    const startTime = timing.navigationStart;
    for (const name of Object.keys(timing)) {
      timing[name] = timing[name] ? timing[name] - startTime : timing[name];
    }

    // track basic page load events correlating to `load` and `domContentLoaded` events
    // see: https://hpbn.co/primer-on-web-performance/#synthetic-and-real-user-performance-measurement
    trackProps[`page_redirect_ms`] = timing.redirectEnd;
    trackProps[`page_tcp_setup_ms`] = timing.connectEnd - timing.redirectEnd;
    trackProps[`page_first_byte_ms`] = timing.responseStart - timing.connectEnd;
    trackProps[`page_dom_content_loaded_ms`] = timing.domContentLoadedEventStart - timing.responseStart;
    trackProps[`page_load_ms`] = timing.loadEventStart - timing.domContentLoadedEventStart;
    trackProps[`page_time_to_first_byte_ms`] = timing.responseStart;
    trackProps[`page_time_to_dom_content_loaded_ms`] = timing.domContentLoadedEventStart;
    trackProps[`page_time_to_load_ms`] = timing.loadEventStart;
    trackProps[`page_navigation_timing`] = timing;

    const pageNav: any = performance.getEntriesByType(`navigation`)[0];
    const serverTiming = pageNav && pageNav.serverTiming;
    if (serverTiming && serverTiming.length) {
      trackProps[`server_response_ms`] = Math.round(serverTiming[0].duration);
    }

    const firstPaint = performance.getEntriesByName(`first-contentful-paint`, `paint`)[0];
    if (firstPaint) {
      trackProps[`page_time_to_first_contentful_paint_ms`] = Math.round(firstPaint.startTime);
    }

    // track time to init and interactive for iron apps
    const initAppStart = performance.getEntriesByName(`init-app:start`, `mark`)[0];
    const initAppEnd = performance.getEntriesByName(`init-app:end`, `mark`)[0];
    if (initAppStart) {
      trackProps[`app_time_to_init_ms`] = Math.round(initAppStart.startTime);
    }
    if (initAppEnd) {
      trackProps[`app_init_ms`] = Math.round(initAppEnd.startTime - initAppStart.startTime);
    }

    const uiReady = performance.getEntriesByName(`ui-ready:end`, `mark`)[0];
    if (uiReady) {
      let ttuir = uiReady.startTime;
      const docHidden = performance.getEntriesByName(`doc-hidden`, `measure`)[0];
      if (docHidden) {
        // when tab is in background or minimized, requestAnimationFrame isn't called and rendering is paused.
        // we account the timing error that by subtracting the time that the document was hidden from ttuir.
        ttuir -= docHidden.duration;
      }
      trackProps[`page_time_to_ui_ready_ms`] = Math.round(ttuir);
    }

    // @ts-ignore - performance.memory is only supported by chrome and not part of lib.d.ts
    const perfMemory = performance.memory;
    if (perfMemory && perfMemory.usedJSHeapSize) {
      trackProps[`memory_used_js_heap_mb`] = Math.round(perfMemory.usedJSHeapSize / 1e6);
    }

    // track network resource stats by domain and type - less bytes to load and parse means faster loading
    const networkResources = performance
      .getEntriesByType(`resource`)
      .concat(performance.getEntriesByType(`navigation`));
    const mixpanelDomainsRe = /.*?(cdn?\.mxpnl|api(-js)?\.mixpanel|mixpanel)\.com$/;
    const stats = {};
    const resourceTypes = {script: `js`, link: `css`, xmlhttprequest: `fetch`, fetch: `fetch`, navigation: `page`};
    const initiatorTypeToResourceType = (initiatorType) => resourceTypes[initiatorType] || `other`;
    const incrStat = (name, metric, value = 1) => {
      if (!(name in stats)) {
        stats[name] = {};
      }
      if (!(metric in stats[name])) {
        stats[name][metric] = value;
      } else {
        stats[name][metric] += value;
      }
    };

    for (const resource of networkResources) {
      try {
        const {
          name: url,
          startTime: resourceStartTime,
          transferSize,
          initiatorType,
          responseEnd,
          decodedBodySize,
        } = resource as any;
        const transferTime = responseEnd - resourceStartTime;
        const resourceType = initiatorTypeToResourceType(initiatorType);
        let host = new URL(url).host;
        // convert cdn.mixpanel.com -> cdn_mixpanel (infra metrics use _ notation as standard)
        host = mixpanelDomainsRe.exec(host) ? host.replace(mixpanelDomainsRe, `$1`).replace(`.`, `_`) : `external`;
        // keep api_mixpanel as host name for historical anlysis purposes
        host = host === `api-js_mixpanel` ? `api_mixpanel` : host;

        for (const level of [`total`, host, `type_${resourceType}`]) {
          incrStat(level, `response_count`);
          incrStat(level, `response_transfer_time`, transferTime);
          incrStat(level, `response_transfer_size`, transferSize);
          incrStat(level, `response_decoded_size`, decodedBodySize);
        }
      } catch {
        reporter.warn(`Failed to track response meta for resource: ${resource.name}`);
      }
    }

    for (const suffix of Object.keys(stats)) {
      for (const metric of Object.keys(stats[suffix])) {
        trackProps[`${metric}_${suffix}`] = Math.round(stats[suffix][metric]);
      }
    }
  }

  return trackProps;
}

/**
 * Track "report loaded" events with perf props for pageload / UI ready
 * @param projectId Mixpanel project
 * @param tab Report name
 * @param extraProps Extra tracking properties for MP events
 */
export async function trackReportLoadPerf(projectId: number | string, tab: string, extraProps = {}) {
  // see https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
  const docHiddenMeasure = new PerfMeasure(`doc-hidden`);
  const handleDocVisibilityChange = () => (document.hidden ? docHiddenMeasure.start() : docHiddenMeasure.end());
  document.addEventListener(`visibilitychange`, handleDocVisibilityChange);
  document.hidden && docHiddenMeasure.start();

  const uiReadyMeasure = new PerfMeasure(`ui-ready`).start();
  const loadPromise = new Promise((resolve) => window.addEventListener(`load`, resolve));
  await new Promise((resolve) => window.addEventListener(`DOMContentLoaded`, resolve));

  await waitUntilUiReady();
  docHiddenMeasure.end();
  uiReadyMeasure.end();
  document.removeEventListener(`visibilitychange`, handleDocVisibilityChange);
  await loadPromise;

  const navPerfProps = getPageLoadPerfProps();
  const trackProps = {
    project_id: projectId,
    tab,
    ...navPerfProps,
    ...extraProps,
  };

  mixpanel.track(`Report Loaded`, trackProps);
  mixpanel.infra_metrics.track(`report-loaded`, {
    distinct_id: projectId,
    ...trackProps,
  });
}

export function logPanelSlowRenders() {
  const getSourceComponent = (component: string) => ({
    sourceComponent: component.includes(`#`) ? component.substring(0, component.indexOf(`#`)) : component,
  });
  const getCommonSlowProps = () => ({
    now: getNow(),
    distinct_id: getProjectId(),
    project_id: getProjectId(),
    workspace_id: getWorkspaceId() || undefined,
    report: getReportName(),
  });
  window.addEventListener(
    `slowRender`,
    (ev: CustomEvent<{comparedToLast?: number; comparedToSlowest?: number; elapsedMs: number; component: string}>) => {
      const {component, elapsedMs} = ev.detail;

      // We know we have render perf issues, and as an attempt to cut down overall noise, going to do a rough
      // exclusion here while individual component values get adjusted. Each component can control their own
      // idea of "slow" by setting a `slowThreshold` config value.
      if (elapsedMs > 100) {
        mixpanel.infra_metrics.track(`[${PERFORMANCE_EVENT_PREFIX}] Slow Render`, {
          ...ev.detail,
          ...getSourceComponent(component),
          ...getCommonSlowProps(),
        });
      }
    },
  );
  window.addEventListener(`componentInitialized`, (ev: CustomEvent<{elapsedMs: number; component: string}>) => {
    const {component, elapsedMs} = ev.detail;

    // Don't track any fast component initializations
    if (elapsedMs > 15 && Math.random() < 0.01) {
      mixpanel.infra_metrics.track(`[${PERFORMANCE_EVENT_PREFIX}] Slow Initialization`, {
        ...ev.detail,
        ...getSourceComponent(component),
        ...getCommonSlowProps(),
      });
    }
  });
}
